<template lang="pug">
b-modal#approval-detail-modal(
  title="Request Details"
  size="xl"
  centered
  ok-only
  ok-title="Close"
  @shown="onModalShown"
  @hidden="onModalHidden"
)
  template(#modal-header)
    h5.modal-title
      Icon(icon="tabler:file-text")
      | Request Details

  .text-center.py-5(v-if="!request")
    p.text-muted No request selected

  .detail-content(v-else)
    //- Status Badge
    .mb-4.d-flex.justify-content-between.align-items-center
      span.status-badge(:class="getStatusClass(request.crb5c_requeststatus)")
        | {{ getStatusText(request.crb5c_requeststatus) }}
      small.text-muted
        | Request ID: {{ request.crb5c_fowsessionmakeupid.slice(0, 8) }}

    //- Client Information
    b-card.mb-3(
      header="Client Information"
      header-bg-variant="light"
      header-text-variant="dark"
    )
      b-row
        b-col(md="6")
          .info-item
            strong Name:
            span {{ request.crb5c_ClientLookup?.crb5c_no || 'N/A' }}
        //- b-col(md="6")
        //-   .info-item
        //-     strong NRIC:
        //-     span {{ request.crb5c_ClientLookup?.crb5c_nric || 'N/A' }}
        b-col(md="6" v-if="request.crb5c_ClientLookup?.crb5c_email")
          .info-item
            strong Email:
            span {{ request.crb5c_ClientLookup.crb5c_email }}

    //- Request Type and Details
    b-card.mb-3(
      header="Request Information"
      header-bg-variant="light"
      header-text-variant="dark"
    )
      .info-item
        strong Request Type:
        span.type-badge(:class="request.crb5c_requesttype === 0 ? 'swap' : 'mc'")
          Icon(:icon="request.crb5c_requesttype === 0 ? 'tabler:repeat' : 'tabler:medical-cross'")
          | {{ request.crb5c_requesttype === 0 ? 'Session Swap' : request.crb5c_requesttype === 2 ? 'Other Request' : 'Medical Certificate Request' }}

      .info-item
        strong Submission Date:
        span {{ formatDate(request.createdon) }}

      .info-item(v-if="request.crb5c_requesttype !== 0 && mcDatesText")
        strong MC Date(s):
        span {{ mcDatesText }}

      .info-item
        strong Reason:
        p.reason-text {{ request.crb5c_reason || 'No reason provided' }}

    //- Session Details for Swap
    b-card.mb-3(
      v-if="request.crb5c_requesttype === 0"
      header="Session Details"
      header-bg-variant="light"
      header-text-variant="dark"
    )
      b-row
        b-col(md="6")
          h6.text-danger Original Session
          .session-details
            .info-item
              strong Date:
              span {{ formatDateOnly(request.crb5c_originaldate) }}
            .info-item(v-if="request.crb5c_ScheduleLookup")
              strong Schedule:
              span {{ request.crb5c_ScheduleLookup.crb5c_name || 'N/A' }}
            .info-item(v-if="request.crb5c_ScheduleLookup")
              strong Time:
              span {{ formatTime(request.crb5c_ScheduleLookup) }}
            .info-item(v-if="request.crb5c_ScheduleLookup")
              strong Day:
              span {{ getDayName(request.crb5c_ScheduleLookup.crb5c_dayofweek) }}
        b-col(md="6")
          h6.text-success Replacement Session
          .session-details
            .info-item
              strong Date:
              span {{ formatDateOnly(request.crb5c_replacementdate) }}
            .info-item(v-if="request.crb5c_ReplacementScheduler")
              strong Schedule:
              span {{ request.crb5c_ReplacementScheduler.crb5c_name || 'N/A' }}
            .info-item(v-if="request.crb5c_ReplacementScheduler")
              strong Time:
              span {{ formatTime(request.crb5c_ReplacementScheduler) }}
            .info-item(v-if="request.crb5c_ReplacementScheduler")
              strong Day:
              span {{ getDayName(request.crb5c_ReplacementScheduler.crb5c_dayofweek) }}

    //- Document for MC Request
    b-card.mb-3(
      v-if="request.crb5c_requesttype === 1 && request.crb5c_file"
      header="Medical Certificate"
      header-bg-variant="light"
      header-text-variant="dark"
    )
      .document-section
        .file-info.mb-3(v-if="fileInfo")
          .info-item
            strong Filename:
            span {{ fileInfo.filename }}
          .info-item
            strong File Size:
            span {{ formatFileSize(fileInfo.filesize) }}
          .info-item
            strong Type:
            span {{ fileInfo.mimetype }}
        
        //- Action Buttons
        .document-actions.mb-3
          b-button.mr-2(
            variant="primary"
            @click="viewDocument"
            :disabled="loadingDoc"
          )
            b-spinner(small v-if="loadingDoc")
            Icon(icon="tabler:eye" v-else)
            | View Document
          b-button(
            variant="outline-primary"
            @click="downloadDocument"
            :disabled="loadingDoc"
          )
            b-spinner(small v-if="loadingDoc")
            Icon(icon="tabler:download" v-else)
            | Download
        
        //- Document Preview
        .document-preview.mt-3(v-if="documentUrl")
          .preview-header.d-flex.justify-content-between.align-items-center.mb-2
            strong Document Preview:
            b-button(size="sm" variant="outline-secondary" @click="closePreview")
              Icon(icon="tabler:x")
              | Close Preview
          
          //- Image Preview
          .image-preview(v-if="isImage")
            img.preview-image(:src="documentUrl" :alt="fileInfo.filename")
          
          //- PDF/Document Preview
          .pdf-preview(v-else)
            .text-center.mb-3
              p.mb-2
                Icon(icon="tabler:file-type-pdf" width="48" height="48")
              p.text-muted
                | PDF documents may not display in all browsers.
                br
                | Click the button below to open in a new tab.
              b-button(variant="primary" @click="downloadDocument" size="sm")
                Icon(icon="tabler:external-link")
                | Open in New Tab
            iframe.preview-iframe(:src="documentUrl" @error="handleIframeError")
            p.text-muted.text-center.mt-2
              small If the document doesn't display above, use the "Open in New Tab" button.

    //- Credit Note outcome (MC / Other only). A date earns a CN only when it is
    //- on an already-issued FOW invoice; other dates get no CN. Read-only — the
    //- CN report itself is generated in the billing app for finance.
    b-card.mb-3(
      v-if="request.crb5c_requesttype !== 0 && request.crb5c_requeststatus !== 2"
      header="Credit Note Outcome"
      header-bg-variant="light"
      header-text-variant="dark"
    )
      .text-center(v-if="estimateLoading")
        b-spinner(small)
        span.ml-2 Checking invoiced dates...
      div(v-else-if="estimateError")
        span.text-muted Could not load the credit estimate. {{ estimateError }}
      div(v-else-if="estimate")
        .info-item(v-if="estimate.willReceiveCreditNote")
          b-badge.p-2.mb-2(variant="success")
            Icon.mr-1(icon="tabler:receipt-refund")
            | Client WILL receive a credit note — estimated ${{ Number(estimate.estimatedCredit).toFixed(2) }} (incl. GST)
          ul.mb-2
            li(v-for="line in estimate.lines" :key="line.invoiceRef")
              strong {{ line.invoiceRef }}
              |  — {{ line.matchedDates.length }} session(s): {{ formatDateList(line.matchedDates) }} → ${{ Number(line.amount).toFixed(2) }}
        .info-item(v-if="estimate.notInvoicedDates && estimate.notInvoicedDates.length")
          b-badge.p-2.mb-2(variant="secondary")
            Icon.mr-1(icon="tabler:calendar-off")
            | No credit note for {{ estimate.notInvoicedDates.length }} date(s) — not on any issued FOW invoice yet
          div
            small.text-muted {{ formatDateList(estimate.notInvoicedDates) }} — these will be picked up by a later CN report if they get invoiced.
        .info-item(v-if="estimate.alreadyReportedDates && estimate.alreadyReportedDates.length")
          b-badge.p-2.mb-1(style="background-color: #50276b")
            Icon.mr-1(icon="tabler:send")
            | Handled — {{ estimate.alreadyReportedDates.length }} date(s) sent to finance (CN listing) or excluded from the invoice
          div
            small.text-muted {{ formatDateList(estimate.alreadyReportedDates) }}
        .info-item(v-if="!estimate.willReceiveCreditNote && (!estimate.notInvoicedDates || !estimate.notInvoicedDates.length)")
          b-badge.p-2(variant="success")
            Icon.mr-1(icon="tabler:circle-check")
            | Fully handled — every MC date was credited via the CN listing or excluded from billing

    //- Rejection Reason (if rejected)
    b-card.mb-3(
      v-if="request.crb5c_requeststatus === 2 && request.crb5c_rejectionreason"
      header="Rejection Information"
      header-bg-variant="light"
      header-text-variant="dark"
    )
      .info-item
        strong Rejection Reason:
        p.reason-text {{ request.crb5c_rejectionreason }}

    //- Action Buttons. Swaps (type 0) are auto-approved and applied by the swap
    //- processor, so they never get an Approve button — only a cancel (Reject)
    //- while the swap hasn't been applied to attendance yet. Approve is greyed
    //- out when the tiered policy says the logged-in admin can't approve this
    //- amount (the backend enforces it too).
    div(v-if="canApprove || canReject")
      .action-buttons.mt-4
        b-button.mr-3(
          v-if="canApprove"
          variant="success"
          size="lg"
          :disabled="approveBlocked"
          :title="approveBlockedReason || ''"
          @click="handleApprove"
        )
          Icon(icon="tabler:check")
          | Approve Request
        b-button(v-if="canReject" variant="danger" size="lg" @click="handleReject")
          Icon(icon="tabler:x")
          | {{ request.crb5c_requesttype === 0 ? 'Cancel Swap' : 'Reject Request' }}
      .text-center.mt-2(v-if="canApprove && approveBlocked")
        small.text-danger
          Icon(icon="tabler:lock")
          |  {{ approveBlockedReason }}

    .text-muted.text-center.mt-4(v-else)
      Icon(icon="tabler:lock")
      | This request has been processed and cannot be modified.
</template>

<script>
import { Icon } from '@iconify/vue2';
import { mapActions } from 'vuex';
import axios from 'axios';
import { MAKEUP_REQUESTS_NAMESPACE } from '@/store/Modules/MakeupRequests/types-makeup-requests';
import { ACTIONS } from '@/store/Modules/MakeupRequests/types-makeup-requests';
import dayjs from 'dayjs';

export default {
  name: 'ApprovalDetailModal',
  components: {
    Icon
  },
  props: {
    request: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      downloadingDoc: false,
      loadingDoc: false,
      documentUrl: null,
      fileInfo: null,
      estimate: null,
      estimateLoading: false,
      estimateError: null
    };
  },
  computed: {
    isImage() {
      if (!this.fileInfo) return false;
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
      return imageTypes.includes(this.fileInfo.mimetype?.toLowerCase());
    },

    // Only pending (status 0) MC / Other requests can be approved. Swaps (type 0)
    // are auto-approved and applied by the swap processor — manually approving one
    // would move it off status 4 and the processor would never apply it.
    canApprove() {
      const r = this.request;
      return !!r && r.crb5c_requesttype !== 0 && r.crb5c_requeststatus === 0;
    },

    // Pending MC / Other requests can be rejected; a submitted swap (status 4) can
    // be cancelled via reject as long as it hasn't been applied to attendance yet.
    canReject() {
      const r = this.request;
      if (!r) return false;
      if (r.crb5c_requeststatus === 0) return true;
      return r.crb5c_requesttype === 0 && r.crb5c_requeststatus === 4 && r.crb5c_swapapplied !== true;
    },

    // Tiered-approval gate for the LOGGED-IN admin, computed server-side and
    // returned with the credit estimate. While the estimate is still loading
    // the button stays enabled — the backend is the real guard either way.
    approveBlocked() {
      return this.estimate?.approval?.allowed === false;
    },

    approveBlockedReason() {
      return this.estimate?.approval?.reason || '';
    },

    // Human-readable summary of the dates an MC/Other request covers.
    mcDatesText() {
      const raw = this.request?.crb5c_mcdaterange;
      if (raw) {
        try {
          const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
          const dates = Array.isArray(obj?.dates) && obj.dates.length ? [...obj.dates].sort() : null;
          if (dates) {
            if (dates.length === 1) return this.formatDateOnly(dates[0]);
            return `${this.formatDateOnly(dates[0])} – ${this.formatDateOnly(dates[dates.length - 1])} (${dates.length} days)`;
          }
          if (obj?.start && obj?.end) {
            return `${this.formatDateOnly(obj.start)} – ${this.formatDateOnly(obj.end)}`;
          }
        } catch (e) { /* fall through */ }
      }
      return this.request?.crb5c_originaldate ? this.formatDateOnly(this.request.crb5c_originaldate) : '';
    }
  },
  watch: {
    request: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.parseFileInfo();
          this.loadEstimate();
        } else {
          this.fileInfo = null;
          this.estimate = null;
          this.estimateError = null;
        }
      }
    }
  },
  methods: {
    ...mapActions(MAKEUP_REQUESTS_NAMESPACE, {
      getDocument: ACTIONS.GET_DOCUMENT,
      getCreditEstimate: ACTIONS.GET_CREDIT_ESTIMATE
    }),

    // Will this MC get a credit note (dates already on an issued FOW invoice)
    // or not? Read-only lookup; swaps and rejected requests are skipped.
    async loadEstimate() {
      this.estimate = null;
      this.estimateError = null;
      const r = this.request;
      if (!r || r.crb5c_requesttype === 0 || r.crb5c_requeststatus === 2) return;
      this.estimateLoading = true;
      try {
        this.estimate = await this.getCreditEstimate(r.crb5c_fowsessionmakeupid);
      } catch (error) {
        this.estimateError = error?.response?.data?.error || '';
      } finally {
        this.estimateLoading = false;
      }
    },

    formatDateList(dates) {
      const list = dates || [];
      // Long runs read better as a range summary than a wall of dates.
      if (list.length > 6) {
        return `${this.formatDateOnly(list[0])} – ${this.formatDateOnly(list[list.length - 1])} (${list.length} dates)`;
      }
      return list.map((d) => this.formatDateOnly(d)).join(', ');
    },
    
    parseFileInfo() {
      if (this.request?.crb5c_file) {
        try {
          // crb5c_file is a JSON ARRAY of documents (legacy rows hold a single
          // object) — show the first document's details.
          const parsed = JSON.parse(this.request.crb5c_file);
          this.fileInfo = Array.isArray(parsed) ? parsed[0] || null : parsed;
        } catch (e) {
          console.error('Error parsing file info:', e);
          this.fileInfo = null;
        }
      }
    },
    
    onModalShown() {
      console.log("Modal shown with request:", this.request);
      this.parseFileInfo();
    },
    
    onModalHidden() {
      console.log("Modal hidden");
      this.fileInfo = null;
      this.documentUrl = null;
      this.loadingDoc = false;
    },
    
    async viewDocument() {
      if (!this.request?.crb5c_fowsessionmakeupid) {
        console.error("No request ID available");
        return;
      }
      
      this.loadingDoc = true;
      try {
        const data = await this.getDocument(this.request.crb5c_fowsessionmakeupid);
        
        // Check file type from fileInfo
        const mimetype = this.fileInfo?.mimetype?.toLowerCase() || '';
        const isImageFile = mimetype.includes('image/');
        
        // If it's an image, show preview in modal — fetched through the API as a
        // data URL (SharePoint download links refuse to be embedded).
        if (isImageFile) {
          try {
            const res = await axios.get(data.downloadUrl, { responseType: 'blob', withCredentials: false });
            const blob = res.data.type ? res.data : new Blob([res.data], { type: mimetype });
            this.documentUrl = URL.createObjectURL(blob);
          } catch (blobErr) {
            const { data: content } = await axios.get(
              `/api/makeup-requests/${this.request.crb5c_fowsessionmakeupid}/document`,
              { params: { content: 0 } }
            );
            this.documentUrl = content?.data?.dataUrl || data.downloadUrl;
          }
          
          // Scroll to preview after setting URL
          this.$nextTick(() => {
            try {
              const modalBody = document.querySelector('#approval-detail-modal .modal-body');
              const previewElement = modalBody?.querySelector('.document-preview');
              if (previewElement) {
                previewElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            } catch (e) {
              console.log('Scroll error (non-critical):', e);
            }
          });
        } else {
          // For PDFs and other documents, open directly in new tab
          window.open(data.downloadUrl, '_blank');
          this.$bvToast.toast('PDF opened in new tab', {
            title: 'Success',
            variant: 'info',
            solid: true,
            autoHideDelay: 2000
          });
        }
      } catch (error) {
        console.error("Error loading document:", error);
        this.$bvToast.toast('Failed to load document', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loadingDoc = false;
      }
    },
    
    closePreview() {
      this.documentUrl = null;
    },
    
    handleIframeError() {
      console.log('Iframe failed to load document');
    },
    
    async downloadDocument() {
      if (!this.request?.crb5c_fowsessionmakeupid) {
        console.error("No request ID available");
        return;
      }
      
      this.downloadingDoc = true;
      try {
        const data = await this.getDocument(this.request.crb5c_fowsessionmakeupid);
        window.open(data.downloadUrl, '_blank');
      } catch (error) {
        this.$bvToast.toast('Failed to retrieve document', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.downloadingDoc = false;
      }
    },
    
    handleApprove() {
      this.$emit('approve', this.request);
      this.$bvModal.hide('approval-detail-modal');
    },
    
    handleReject() {
      this.$emit('reject', this.request);
      this.$bvModal.hide('approval-detail-modal');
    },
    
    getStatusClass(status) {
      return {
        'status-pending': status === 0,
        'status-approved': status === 1,
        'status-rejected': status === 2,
        'status-submitted': status === 4
      };
    },
    
    getStatusText(status) {
      const statusMap = {
        0: 'Pending',
        1: 'Approved',
        2: 'Rejected',
        3: 'Re-Apply',
        4: 'Auto-Approved'
      };
      return statusMap[status] || 'Unknown';
    },
    
    formatDate(date) {
      return date ? dayjs(date).format('DD MMM YYYY, hh:mm A') : 'N/A';
    },

    // Session/MC dates are calendar dates — rendering "12:00 AM" on each is noise.
    formatDateOnly(date) {
      return date ? dayjs(date).format('DD MMM YYYY') : 'N/A';
    },

    formatTime(schedule) {
      if (!schedule) return 'N/A';
      const start = schedule.crb5c_starttime || '';
      const end = schedule.crb5c_endtime || '';
      return `${start} - ${end}`;
    },
    
    getDayName(dayNum) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[dayNum] || 'N/A';
    },
    
    formatFileSize(bytes) {
      if (!bytes) return 'N/A';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / 1048576).toFixed(2) + ' MB';
    }
  }
};
</script>

<style scoped>
.detail-content {
  padding: 0.5rem 0;
}

/* One quiet, consistent look for every section card — the colored rainbow
   headers made the modal noisy; section identity comes from the title text. */
.detail-content ::v-deep .card {
  border: 1px solid #ececf1;
  border-radius: 10px;
  box-shadow: none;
}

.detail-content ::v-deep .card-header {
  background-color: #fafafc !important;
  border-bottom: 1px solid #ececf1;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280 !important;
  padding: 0.6rem 1.25rem;
}

.info-item {
  margin-bottom: 0.75rem;
}

.info-item strong {
  display: inline-block;
  min-width: 120px;
  color: #495057;
}

.reason-text {
  margin-top: 0.5rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.status-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.status-approved {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-rejected {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.status-submitted {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-weight: 500;
}

.type-badge.swap {
  background-color: #e7f3ff;
  color: #0066cc;
}

.type-badge.mc {
  background-color: #ffe7e7;
  color: #cc0000;
}

.session-details {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 0.5rem;
}

.document-section {
  padding: 0.5rem 0;
}

.file-info {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.document-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
}

.document-preview {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.image-preview {
  text-align: center;
}

.preview-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-iframe {
  width: 100%;
  height: 600px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: white;
}

.close-preview-btn {
  margin-bottom: 1rem;
}
</style>
